import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useRef } from "react";
import LogLayout from "../components/LogLayout";
import {
  MessageSquare, Send, ArrowLeft, Search, Clock, Building2,
  FileText, ChevronRight, User, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

function ThreadList({ threads, selectedId, onSelect, loading }) {
  const [search, setSearch] = useState("");

  const filtered = threads.filter(t =>
    !search ||
    (t.invoice_number || "").toLowerCase().includes(search.toLowerCase()) ||
    (t.insurer_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="thread-list" className="flex flex-col h-full">
      <div className="p-3 border-b border-white/5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="thread-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9 bg-slate-900/60 border-white/5 text-white text-sm h-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Aucune conversation</p>
            <p className="text-xs text-slate-600 mt-1">Les messages apparaitront ici quand un assureur communiquera sur une facture</p>
          </div>
        ) : filtered.map(t => (
          <button
            key={t.invoice_id}
            data-testid={`thread-${t.invoice_id}`}
            onClick={() => onSelect(t)}
            className={`w-full text-left p-3.5 border-b border-white/5 transition-all ${
              selectedId === t.invoice_id
                ? "bg-sky-500/10 border-l-2 border-l-sky-500"
                : "hover:bg-white/5"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-white truncate">{t.invoice_number || "Facture"}</span>
                  {t.unread > 0 && (
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {t.unread}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" />
                  {t.insurer_name}
                  <span className="text-slate-600">-</span>
                  <span className="text-emerald-400">{fmt(t.amount)} FCFA</span>
                </p>
                {t.last_message && (
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    <span className="text-slate-400">{t.last_sender}:</span> {t.last_message}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] text-slate-600">
                  {t.last_date ? new Date(t.last_date).toLocaleDateString("fr-FR") : ""}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatView({ thread, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!thread) return;
    try {
      const res = await axios.get(`${API}/insurance/messages/${thread.invoice_id}`, { headers: getHeaders() });
      setMessages(res.data.messages || []);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [thread]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/insurance/messages`, {
        invoice_id: thread.invoice_id,
        content: newMsg.trim()
      }, { headers: getHeaders() });
      setNewMsg("");
      await loadMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi du message");
    } finally {
      setSending(false);
    }
  };

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Selectionnez une conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="chat-view" className="flex flex-col h-full">
      {/* Chat header */}
      <div className="p-4 border-b border-white/5 bg-slate-900/40">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="md:hidden text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-lg bg-sky-500/15 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-sky-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-white truncate">
              {thread.invoice_number} - {thread.insurer_name}
            </h3>
            <p className="text-xs text-slate-500">
              {fmt(thread.amount)} FCFA - {thread.total_messages} message(s)
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Aucun message dans cette conversation</p>
            <p className="text-xs text-slate-600 mt-1">Envoyez le premier message ci-dessous</p>
          </div>
        ) : messages.map(m => {
          const isOwn = m.sender_role === "pharmacy";
          return (
            <div key={m.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] ${isOwn ? "order-last" : ""}`}>
                <div className={`rounded-2xl px-4 py-2.5 ${
                  isOwn
                    ? "bg-sky-500/20 border border-sky-500/20 rounded-br-md"
                    : "bg-slate-800 border border-white/5 rounded-bl-md"
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11px] font-semibold ${isOwn ? "text-sky-400" : "text-amber-400"}`}>
                      {m.sender_name}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                      isOwn ? "bg-sky-500/10 text-sky-500" : "bg-amber-500/100/10 text-amber-500"
                    }`}>
                      {m.sender_role === "pharmacy" ? "Pharmacie" : m.sender_role === "assurance" ? "Assureur" : "Admin"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-200 leading-relaxed">{m.content}</p>
                </div>
                <p className={`text-[10px] text-slate-600 mt-1 ${isOwn ? "text-right" : "text-left"}`}>
                  {new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {m.read && !isOwn && <span className="ml-1 text-emerald-600">Lu</span>}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/5 bg-slate-900/40">
        <div className="flex gap-2">
          <Input
            data-testid="chat-message-input"
            value={newMsg}
            onChange={e => setNewMsg(e.target.value)}
            placeholder="Ecrivez votre message..."
            className="flex-1 bg-slate-900/60 border-white/5 text-white text-sm h-10"
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
          />
          <Button
            data-testid="chat-send-btn"
            onClick={handleSend}
            disabled={!newMsg.trim() || sending}
            className="bg-sky-500 hover:bg-sky-400 text-white h-10 px-4"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LogMessagesPage() {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/insurance/threads`, { headers: getHeaders() });
      setThreads(res.data.threads || []);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
    const interval = setInterval(loadThreads, 30000);
    return () => clearInterval(interval);
  }, [loadThreads]);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <LogLayout title="Messagerie Assureurs" subtitle="Communication sécurisée avec les compagnies d'assurance">
      <div data-testid="messages-page" className="space-y-4">
        {/* Header stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <MessageSquare className="w-4 h-4 text-sky-400" />
              <span>{threads.length} conversation(s)</span>
              {totalUnread > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 text-xs font-semibold">
                  {totalUnread} non lu(s)
                </span>
              )}
            </div>
          </div>
          <Button data-testid="refresh-threads-btn" onClick={loadThreads} variant="outline" size="sm" className="border-white/10 text-slate-400 text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Actualiser
          </Button>
        </div>

        {/* Chat interface */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden" style={{ height: "calc(100vh - 220px)", minHeight: "500px" }}>
          <div className="flex h-full">
            {/* Thread list */}
            <div className={`w-full md:w-80 border-r border-white/5 flex-shrink-0 ${selectedThread ? "hidden md:flex md:flex-col" : "flex flex-col"}`}>
              <ThreadList
                threads={threads}
                selectedId={selectedThread?.invoice_id}
                onSelect={setSelectedThread}
                loading={loading}
              />
            </div>

            {/* Chat area */}
            <div className={`flex-1 ${!selectedThread ? "hidden md:flex md:flex-col" : "flex flex-col"}`}>
              <ChatView
                thread={selectedThread}
                onBack={() => setSelectedThread(null)}
              />
            </div>
          </div>
        </div>
      </div>
    </LogLayout>
  );
}
